import { isPlatformBrowser } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { FileOpener } from '@awesome-cordova-plugins/file-opener/ngx';
import { PreviewAnyFile } from '@awesome-cordova-plugins/preview-any-file/ngx';
import { Media } from '@capacitor-community/media';
import { Share } from '@capacitor/share';
import { Pwa } from '@freelancer/pwa';
import { TimeUtils } from '@freelancer/time-utils';
import { trackById } from '@freelancer/ui';
import {
  ToastAlertColor,
  ToastAlertService,
  ToastAlertType,
} from '@freelancer/ui/toast-alert';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { Subscription } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import type { DownloadTask } from './file-download.service';
import { FileDownload, FileDownloadError } from './file-download.service';

@UntilDestroy({ className: 'FileDownloadAlertComponent' })
@Component({
  selector: 'fl-file-download-alert',
  template: `
    <ng-container *ngFor="let download of downloads; trackBy: trackById">
      <fl-toast-alert
        *ngIf="download.changes$ | flAsync as changes"
        [id]="'fl-download-alert-' + download.id"
        [timeout]="undefined"
        [color]="ToastAlertColor.LIGHT"
        [type]="
          changes.status === 'running'
            ? ToastAlertType.LOADING
            : changes.status === 'success'
              ? ToastAlertType.SUCCESS
              : ToastAlertType.ERROR
        "
        [closeable]="changes.status === 'success' || changes.status === 'error'"
        (close)="handleHide(download)"
      >
        <div class="DownloadAlertBody">
          <ng-container i18n="File downloads notification status">
            Downloading {{ download.name | truncateFilename: 25 }}
          </ng-container>
          <div class="DownloadAlertDetails">
            <div
              class="DownloadAlertDetails--status"
              [flMarginRight]="'xxsmall'"
            >
              <ng-container
                *ngIf="changes.status === 'success'"
                i18n="File download notification status"
              >
                Download complete
              </ng-container>
              <ng-container *ngIf="changes.status === 'error'">
                <ng-container [ngSwitch]="changes.errorCode">
                  <ng-container
                    *ngSwitchCase="FileDownloadError.PERMISSION_NOT_GRANTED"
                    i18n="Permission denied error message for download files"
                  >
                    Permission denied. Please enable permissions in app
                    settings.
                  </ng-container>
                  <ng-container
                    *ngSwitchCase="FileDownloadError.UNKNOWN"
                    i18n="General error message for download files"
                  >
                    Download failed
                  </ng-container>
                  <ng-container
                    *ngSwitchDefault
                    i18n="General error message for download files"
                  >
                    Download failed
                  </ng-container>
                </ng-container>
              </ng-container>
              <fl-progress-bar
                *ngIf="
                  changes.status === 'running' && changes.progressPercentage
                "
                label="Download progress for {{ download.name }}"
                i18n-label="File download notification progress bar"
                [progressPercentage]="changes.progressPercentage"
              ></fl-progress-bar>
            </div>
            <fl-link
              *ngIf="changes.status === 'running'"
              i18n="File download notification button"
              (click)="handleCancel(download)"
            >
              Cancel
            </fl-link>
            <fl-link
              *ngIf="changes.status === 'success'"
              i18n="File download notification button"
              (click)="handleOpen(download, changes.fileUrl)"
            >
              Open file
            </fl-link>
          </div>
        </div>
      </fl-toast-alert>
    </ng-container>
  `,
  styleUrls: ['./file-download-alert.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileDownloadAlertComponent implements OnInit, OnDestroy {
  trackById = trackById;

  ToastAlertType = ToastAlertType;
  ToastAlertColor = ToastAlertColor;
  FileDownloadError = FileDownloadError;

  downloads: readonly DownloadTask[] = [];
  /**
   * Whether the app should use Capacitor Media plugin to save directly to the
   * gallery for compatible file types (video/photo), or open the share modal
   * for incompatible file types on download completion.
   * Needed on iOS because files cannot be saved to public locations,
   * whereas on Android we just save the files to /Downloads.
   */
  requiresSaving: boolean;

  private downloadSubscription?: Subscription;

  constructor(
    private fileDownload: FileDownload,
    private toastAlertService: ToastAlertService,
    private changeDetectorRef: ChangeDetectorRef,
    private fileOpener: FileOpener,
    private previewAnyFile: PreviewAnyFile,
    private pwa: Pwa,
    private timeUtils: TimeUtils,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {}

  async ngOnInit(): Promise<void> {
    this.requiresSaving = this.pwa.getPlatform() === 'ios';

    if (isPlatformBrowser(this.platformId) && this.pwa.isNative()) {
      this.downloadSubscription = this.fileDownload
        .getDownloadTaskStream()
        .subscribe(download => {
          this.downloads = [...this.downloads, download];
          // setTimeout to wait for the toast to render first.
          setTimeout(() => {
            this.toastAlertService.openById(`fl-download-alert-${download.id}`);
          });

          // subscribe to the finish event
          firstValueFrom(
            download.changes$.pipe(
              filter(change => change.status !== 'running'),
              untilDestroyed(this),
            ),
          ).then(event => {
            if (event.status === 'success') {
              if (this.requiresSaving) {
                // if file needs to be manually saved, trigger share modal
                this.handleSave(download, event.fileUrl);
              } else {
                // otherwise just hide alert after a short delay
                this.timeUtils.setTimeout(() => {
                  this.handleHide(download);
                }, 5000);
              }
            }
          });

          this.changeDetectorRef.markForCheck();
        });
    }
  }

  ngOnDestroy(): void {
    if (this.downloadSubscription) {
      this.downloadSubscription.unsubscribe();
    }
  }

  handleCancel(download: DownloadTask): void {
    download.cancel();
    this.handleHide(download);
  }

  handleHide(download: DownloadTask): void {
    this.toastAlertService.close(`fl-download-alert-${download.id}`);
    this.downloads = this.downloads.filter(task => task.id !== download.id);
  }

  async handleSave(download: DownloadTask, fileUrl: string): Promise<void> {
    const mimeType = await firstValueFrom(
      download.mimeType$.pipe(untilDestroyed(this)),
    );
    const useCapacitorMedia =
      mimeType.startsWith('video/') || mimeType.startsWith('image/');

    // Timeout to be set dependent on if the user will save or share.
    const timeout = useCapacitorMedia ? 5000 : 1000;

    if (useCapacitorMedia) {
      if (mimeType.startsWith('video/')) {
        Media.saveVideo({ path: fileUrl });
      } else if (mimeType.startsWith('image/')) {
        Media.savePhoto({ path: fileUrl });
      }
    } else {
      await Share.share({
        title: download.name,
        url: fileUrl,
      }).catch(error => {
        // User changes their mind. Ignore the error.
        if (error.message === 'Share canceled') {
          return;
        }
        throw error;
      });
    }

    // If the user shared, we can close after a short delay.
    this.timeUtils.setTimeout(() => {
      this.handleHide(download);
    }, timeout);
  }

  async handleOpen(download: DownloadTask, fileUrl: string): Promise<void> {
    // If we're in iOS, then open it up in the file preview window.
    if (this.pwa.getPlatform() === 'ios') {
      await firstValueFrom(
        this.previewAnyFile.previewPath(fileUrl).pipe(untilDestroyed(this)),
      );
    } else if (this.pwa.getPlatform() === 'android') {
      // Otherwise, on Android use the file opener.
      const mimeType = await firstValueFrom(
        download.mimeType$.pipe(untilDestroyed(this)),
      );
      this.fileOpener.showOpenWithDialog(
        fileUrl,
        mimeType ||
          // In the case there's no mime type and it is running on Android,
          // attempt to open it in a file manager.
          'vnd.android.document/directory',
      );
    }

    // Close the prompt shortly after the user has opened.
    this.timeUtils.setTimeout(() => {
      this.handleHide(download);
    }, 1000);
  }
}
